import React from 'react'
import PropTypes from 'prop-types'
import {Box, Stack, Flex, Text, Button, Image, useMultiStyleConfig} from '@chakra-ui/react'
import {FormattedMessage, FormattedNumber, useIntl} from 'react-intl'
import {Link} from 'react-router-dom'
import InformativeLabel from '../../../components/informative-label'
import {getInfoLabelVariantByOmsStatus, getOrderStatus} from './utils'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useOrderDetailsFromOrder} from '../../../custom-hooks/use-order-details'
import {useProduct} from '@salesforce/commerce-sdk-react'
import {remCalc} from '../../../theme/_utils'

const OrderWidgetCard = ({order}) => {
    const styles = useMultiStyleConfig('Orders')
    const {formatDate, formatMessage} = useIntl()
    const {orderDetails} = useOrderDetailsFromOrder(order)
    const {locale} = useMultiSite()
    const {localCurrency} = useCurrency()
    const currency = order?.CurrencyISOCode || localCurrency
    const localeId = locale?.alias

    const {data: productData} = useProduct({
        parameters: {
            id: orderDetails?.orderItems[0]?.ProductCode,
            allImages: false,
            currency,
        },
    })

    const smallImage = productData?.imageGroups?.find((item) => item.viewType === 'medium')
        ?.images[0]?.link

    if (!order) return
    // Extract shipment statuses
    const shipmentStatuses = order?.fulfillmentOrder?.map((shipment) => shipment?.Status) || []

    // Pass shipmentStatuses to getOrderStatus
    const {status, message} = getOrderStatus(order?.OrderStatus, false, shipmentStatuses)

    const translatedStatus = message && formatMessage(message)
    return (
        <Box {...styles.widgetWrapper}>
            <Image
                {...styles.orderWidgetProductImage}
                src={smallImage}
                alt={`${formatMessage({
                    id: 'acount.order_widget.image_alt_pretext',
                    defaultMessage: 'An image for the product',
                })} ${orderDetails?.orderItems[0]?.Name}`}
            />
            <Box {...styles.orderContainer}>
                <Text {...styles.widgetHeader}>
                    <FormattedMessage
                        id="account_order_history.label.order_number"
                        defaultMessage="Order number: {orderNumber}"
                        values={{
                            orderNumber: order?.OrderNumber?.toUpperCase(),
                        }}
                    />
                    {order?.hasSubscription && ( // verify this field existance
                        <Text
                            as="span"
                            textStyle="body-small"
                            color="text.default"
                            fontWeight="400"
                        >
                            <FormattedMessage
                                id="account_order_history.label.order_has_subscription"
                                defaultMessage="This order contains a subscription"
                            />
                        </Text>
                    )}
                </Text>
                <Flex {...styles.orderDetails}>
                    <Box {...styles.firstColumn} mb={{base: remCalc(8), lg: '0'}}>
                        <Stack {...styles.gaps}>
                            <Text {...styles.bodyMedium}>
                                <FormattedMessage
                                    defaultMessage="Order date"
                                    id="account_order_history.label.ordered"
                                />
                            </Text>
                            <Text {...styles.bodyMedium} fontWeight="700">
                                <FormattedMessage
                                    defaultMessage="{date}"
                                    id="account_order_history.label.date"
                                    values={{
                                        date: formatDate(new Date(order?.OrderedDate), {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                        }),
                                    }}
                                />
                            </Text>
                        </Stack>
                    </Box>
                    {/* Products */}
                    <Box {...styles.secondColumn} mb={{base: remCalc(8), lg: '0'}}>
                        <Stack {...styles.gaps}>
                            <Text {...styles.bodyMedium}>
                                <FormattedMessage
                                    defaultMessage="Products"
                                    id="account_order_history.label.num_of_products"
                                />
                            </Text>
                            {/* Sum of all quantities
                             * This is a sum of all quantities of all products in the order
                             */}
                            <Text {...styles.bodyMedium} fontWeight="700">
                                {order?.OrderTotalQuantity}
                            </Text>
                        </Stack>
                    </Box>
                    {/* Total */}
                    <Box {...styles.firstColumn} mb={{base: remCalc(8), lg: '0'}}>
                        <Stack {...styles.gaps}>
                            <Text {...styles.bodyMedium}>
                                <FormattedMessage
                                    defaultMessage="Total"
                                    id="account_order_history.label.total"
                                />
                            </Text>
                            <Text {...styles.bodyMedium} fontWeight="700">
                                <FormattedNumber
                                    style="currency"
                                    currency={currency}
                                    value={order?.GrandTotalAmount}
                                />
                            </Text>
                        </Stack>
                    </Box>
                    {/* Status */}
                    <Box {...styles.secondColumn} mb={{base: remCalc(8), lg: '0'}}>
                        <Stack {...styles.gaps}>
                            <Text {...styles.bodyMedium}>
                                <FormattedMessage
                                    defaultMessage="Status"
                                    id="account_order_history.label.status"
                                />
                            </Text>
                            <InformativeLabel
                                label={translatedStatus}
                                width={{
                                    base: 'fit-content',
                                    md: 'max-content',
                                }}
                                variant={getInfoLabelVariantByOmsStatus(status)}
                            />
                        </Stack>
                    </Box>
                    {/* Mobile details link */}
                    <Box {...styles.detailsContainerMobile}>
                        <Button
                            as={Link}
                            to={`/account/orders/${order?.Id}/${order?.OrderNumber}/${order?.OriginalOrderId}`}
                            variant="link"
                            {...styles.detailsLink}
                        >
                            <FormattedMessage
                                defaultMessage="Details"
                                id="account_order_history.link.details"
                            />
                        </Button>
                    </Box>
                    {/* Desktop details link */}
                    <Box {...styles.detailsContainerDesktop}>
                        <Button
                            as={Link}
                            to={`/${localeId}/account/orders/${order?.Id}/${order?.OrderNumber}/${order?.OriginalOrderId}`}
                            variant="link"
                            {...styles.detailsLink}
                        >
                            <FormattedMessage
                                defaultMessage="Details"
                                id="account_order_history.link.details"
                            />
                        </Button>
                    </Box>
                </Flex>
            </Box>
        </Box>
    )
}

OrderWidgetCard.propTypes = {
    order: PropTypes.object,
}

export default OrderWidgetCard
